import { useState } from 'react';
import useSWR, { mutate } from 'swr';
import { toast } from 'sonner';
import { CirclePlus } from 'lucide-react';
import { get, put, ApiError } from '@/lib/api';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/layout/PageHeader';
import { SubmissionRow, type Submission } from '@/components/domain/SubmissionRow';
import { SubmissionWorkerDrawer, type SubmissionDetail } from '@/components/domain/SubmissionWorkerDrawer';
import { EditProductDialog } from '@/components/domain/EditProductDialog';
import { NewSubmissionDialog } from '@/components/domain/NewSubmissionDialog';
import { QueueLoadError } from '@/components/domain/QueueLoadError';
import { productOfTier } from '@/lib/prize-tiers';
import {
  fetchAllSubmissions, submissionsPageUrl, truncationNotice,
  type LoadedSubmissions,
} from '@/lib/submission-pages';

// Prize Submissions — the full fundraiser pipeline (all statuses), with the
// action drawer (logo replace/text, tier edits, proof send) on every row and
// native submission creation. Replaced the old raw-field DetailDrawer and the
// duplicate Worker Review submissions page (2026-06-10).

const STATUS_FILTERS = ['', 'pending_worker_review', 'awaiting_rep_decision', 'changes_requested', 'final_approved', 'order_pending', 'cancelled'] as const;
const STATUS_LABEL: Record<string, string> = {
  '': 'All',
  pending_worker_review: 'Pending Review',
  awaiting_rep_decision: 'Awaiting Rep',
  changes_requested: 'Revisions',
  final_approved: 'Approved',
  order_pending: 'Order Pending',
  cancelled: 'Cancelled',
};

export default function Submissions() {
  const [filter, setFilter] = useState<string>('');
  const [q, setQ] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [editTier, setEditTier] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);

  // Every row for the selected filter, not the first 200 of them. The old flat
  // limit of 200 was below several live statuses (measured 2026-08-25:
  // final_approved 453, awaiting_rep_decision 219, 1,824 submissions overall),
  // so those tabs quietly rendered a prefix of the pipeline as if it were all
  // of it. The key stays the first-page URL so refreshSubmission()'s
  // `/api/submissions?` prefix match still revalidates this page.
  const url = submissionsPageUrl(filter ? { status: filter } : {});
  const { data, error, isLoading } = useSWR<LoadedSubmissions<Submission>>(
    url,
    (pageUrl: string) => fetchAllSubmissions<Submission>(pageUrl, get),
  );
  const incomplete = data ? truncationNotice(data) : null;
  const { data: detail, isLoading: detailLoading } = useSWR<SubmissionDetail>(
    openId ? `/api/submissions/${openId}` : null,
    get
  );

  const filtered = (data?.submissions || []).filter((s) => {
    if (!q.trim()) return true;
    const needle = q.toLowerCase();
    return (
      String(s.school_name || '').toLowerCase().includes(needle) ||
      String(s.fundraiserName || '').toLowerCase().includes(needle) ||
      String(s.sportType || '').toLowerCase().includes(needle)
    );
  });

  return (
    <div className="px-7 py-6">
      <PageHeader
        title="Prize Submissions"
        subtitle={`Fundraiser pipeline · ${filtered.length}${q || filter ? ` of ${data?.submissions.length ?? 0}` : ''} shown — open a row to edit logos, products, and send proofs`}
        actions={
          <Button onClick={() => setCreating(true)}>
            <CirclePlus className="h-4 w-4" aria-hidden /> New submission
          </Button>
        }
      />

      {/* Rendered only when the list is provably NOT whole; its absence is a
          positive claim that every matching submission is below. */}
      {incomplete && (
        <div className="mb-3 rounded-sm border border-warning/40 bg-warning/10 px-3 py-2 text-[12px] text-text/85">
          {incomplete}
        </div>
      )}

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1.5">
          {STATUS_FILTERS.map((s) => (
            <button
              key={s || 'all'}
              onClick={() => setFilter(s)}
              className={`rounded-full px-3 py-1 text-[11.5px] font-medium transition-colors ${filter === s ? 'bg-text text-white' : 'border border-black/[0.08] bg-surface text-muted hover:bg-bg'}`}
            >
              {STATUS_LABEL[s]}
            </button>
          ))}
        </div>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by school, fundraiser, sport…" className="max-w-[260px] flex-1" />
      </div>

      <div className="overflow-hidden rounded-[12px] border border-black/[0.05] bg-surface shadow-card">
        {isLoading ? (
          <div className="space-y-2 p-3">
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
          </div>
        ) : error ? (
          <QueueLoadError queue="the submissions list" onRetry={() => void mutate(url)} />
        ) : filtered.length ? (
          filtered.map((s) => (
            <SubmissionRow key={s.id} s={s} onClick={() => setOpenId(s.id)} />
          ))
        ) : (
          <div className="p-10 text-center text-[12px] text-muted">
            No submissions {q ? `match “${q}”` : filter ? `in ${STATUS_LABEL[filter]}` : 'found'}.
          </div>
        )}
      </div>

      <SubmissionWorkerDrawer
        open={!!openId}
        onClose={() => setOpenId(null)}
        submission={detail}
        loading={detailLoading}
        onEditTier={(tier) => setEditTier(tier)}
      />

      <EditProductDialog
        open={editTier !== null}
        onOpenChange={(o) => { if (!o) setEditTier(null); }}
        tier={editTier ?? 1}
        currentProductId={
          editTier != null && detail
            ? productOfTier(detail as unknown as Record<string, unknown>, editTier)
            : undefined
        }
        busy={saving}
        onConfirm={async (newProductId) => {
          if (!openId || !editTier) return;
          setSaving(true);
          try {
            const body = editTier <= 2
              ? { [`tier${editTier}Product`]: newProductId }
              : { tier: editTier, product: newProductId };
            await put(`/api/submissions/${openId}/products`, body);
            toast.success(`Product ${editTier} changed`);
            setEditTier(null);
            await mutate(`/api/submissions/${openId}`);
            await mutate((key: unknown) => typeof key === 'string' && key.startsWith('/api/submissions?'));
          } catch (e) {
            const body = (e as ApiError).body as { message?: string; error?: string } | null;
            toast.error(body?.message || body?.error || 'Save failed');
            await mutate(`/api/submissions/${openId}`);
          } finally {
            setSaving(false);
          }
        }}
      />

      <NewSubmissionDialog
        open={creating}
        onOpenChange={setCreating}
        onCreated={(id) => setOpenId(id)}
      />
    </div>
  );
}
